import { type AuthUser } from "wasp/auth";
import DefaultLayout from "../layout/DefaultLayout";
import { useRedirectHomeUnlessUserIsAdmin } from "../useRedirectHomeUnlessUserIsAdmin";
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PaymentPlanId, prettyPaymentPlanName } from "../../payment/plans";
import { Check, Loader2, Sparkles, CreditCard, ShoppingBag } from "lucide-react";
import {
  getSubscriptions,
  getUserSubscriptions,
  useQuery,
  createUserSubscription,
  getAllPlans,
  updateUserById,
} from "wasp/client/operations";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
  Card,
  CardContent
} from "../../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { analytics } from "../../analytics";
import { chargebeeBackendBaseUrl } from "./urls";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useTranslation } from "../../i18n/hooks";
import { TFunction } from "i18next";
import CloseIcon from "@mui/icons-material/Close";
import { Button as MuiButton, Divider as MuiDivider, IconButton, Stack, Tab } from "@mui/material";
import { Typography } from "@mui/material";

interface PaymentPlanCard {
  name: string;
  price: {
    yearly: string;
    monthly: string;
  };
  description: string;
  features: string[];
  featureTitle: string;
  planId: {
    monthly: string;
    yearly: string;
  };
}

interface Subscription {
  subscription: {
    status: string;
    subscription_items: Array<{
      item_price_id: string;
    }>;
  };
}

interface SubscriptionResponse {
  list: Subscription[];
}

const getPaymentPlanCards = (t: any): Record<PaymentPlanId, PaymentPlanCard> => ({
  [PaymentPlanId.Free]: {
    name: prettyPaymentPlanName(PaymentPlanId.Free),
    price: {
      monthly: "$0",
      yearly: "$0",
    },
    description: t('plans.free_description'),
    features: [
      t('plans.feature_1000_credits'),
      t('plans.feature_ai_content'),
      t('plans.feature_automated_tagging'),
      t('plans.feature_health_scoring'),
    ],
    featureTitle: t('plans.standout_features'),
    planId: {
      monthly: "Free-USD-Monthly",
      yearly: "Free-USD-Yearly",
    },
  },
  [PaymentPlanId.Essential]: {
    name: prettyPaymentPlanName(PaymentPlanId.Essential),
    price: {
      monthly: "$9",
      yearly: "$8",
    },
    description: t('plans.essential_description'),
    features: [
      t('plans.feature_10000_credits'),
      t('plans.feature_ai_content'),
      t('plans.feature_automated_tagging'),
      t('plans.feature_health_scoring'),
    ],
    featureTitle: t('plans.standout_features'),
    planId: {
      monthly: "Essential-USD-Monthly",
      yearly: "Essential-USD-Yearly",
    },
  },
  [PaymentPlanId.Advanced]: {
    name: prettyPaymentPlanName(PaymentPlanId.Advanced),
    price: {
      monthly: "$32",
      yearly: "$29",
    },
    description: t('plans.advanced_description'),
    features: [
      t('plans.feature_100000_credits'),
      t('plans.feature_marketplace_scraping'),
      t('plans.feature_chat_support'),
    ],
    featureTitle: t('plans.essential_plan_plus'),
    planId: {
      monthly: "Advanced-USD-Monthly",
      yearly: "Advanced-USD-Yearly",
    },
  },
});

const AdminUpgradePlanPage = ({ user }: { user: AuthUser }) => {
  useRedirectHomeUnlessUserIsAdmin({ user });
  const { t } = useTranslation();
  const paymentPlanCards = getPaymentPlanCards(t);
  const {
    data: subscriptions,
    isLoading: isLoadingSubscriptions,
    error: subscriptionsError,
  } = useQuery(getUserSubscriptions) as {
    data: any | undefined;
    isLoading: boolean;
    error: Error | null;
  };

  useEffect(() => {
    analytics.sendPricingPageViewedEvent({
      source: "pricing_page",
      email: user.email ?? user.id,
    });
    
    // Kullanıcının mevcut subscriptionType'ına göre previousPaymentMethod'u ayarla
    if (user.subscriptionType === "SHOPIFY") {
      setPreviousPaymentMethod("shopify");
    } else if (user.subscriptionType === "CHARGEBEE") {
      setPreviousPaymentMethod("chargebee");
    }
  }, []);

  const [selectedTab, setSelectedTab] = useState("yearly");
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get("selected-plan");
  const [firstTime, setFirstTime] = useState<boolean>(false);
  const hasShopifyDomain = user.gengageShopifyDomain || user.miqShopifyDomain;
  const [paymentMethod, setPaymentMethod] = useState<"shopify" | "chargebee">(
    "shopify"
  );
  const isDebugMode = localStorage.getItem("debug_mode") === "true";
  
  // Yeni state'ler
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [selectedPlanName, setSelectedPlanName] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [shopifyDisabled, setShopifyDisabled] = useState(false);
  const [chargebeeDisabled, setChargebeeDisabled] = useState(false);
  const [previousPaymentMethod, setPreviousPaymentMethod] = useState<"shopify" | "chargebee" | null>(null);

  useEffect(() => {
    if (selectedPlan) {
      localStorage.removeItem("redirect_url");
      const period = selectedPlan.split("-")[1];
      if (selectedTab !== period.toLowerCase()) {
        setSelectedTab(period.toLowerCase());
      }
      setTimeout(() => {
        const planButton = document.getElementById(selectedPlan);
        if (planButton) {
          planButton.click();
        }
      }, 100);
    }
  }, [selectedPlan, selectedTab]);

  const [loading, setLoading] = useState<boolean>(false);
  
  const handleSelectPlan = (name: string, planId?: string) => {
    setSelectedPlanName(name);
    setSelectedPlanId(planId || "");
    
    // Kullanıcının mevcut subscriptionType'ına göre butonları devre dışı bırak
    if (user.subscriptionType === "SHOPIFY") {
      setShopifyDisabled(false);
      setChargebeeDisabled(true);
    } else if (user.subscriptionType === "CHARGEBEE") {
      setShopifyDisabled(true);
      setChargebeeDisabled(false);
    } else {
      // Kullanıcının henüz bir subscriptionType'ı yoksa, hiçbir seçenek devre dışı değil
      setShopifyDisabled(false);
      setChargebeeDisabled(false);
    }
    
    setIsPaymentDialogOpen(true);
  };
  
  const handlePaymentMethodSelect = async (method: "shopify" | "chargebee") => {
    setLoading(true);
    setPaymentMethod(method);
    
    // Kullanıcının subscriptionType'ını güncelle
    try {
      await updateUserById({
        id: user.id,
        data: { subscriptionType: method === "shopify" ? "SHOPIFY" : "CHARGEBEE" }
      });
      
      // Bir sonraki sefer için bu seçimi hatırla
      setPreviousPaymentMethod(method);
      
      // Analytics olaylarını gönder
      analytics.sendPricingPlanSelectedEvent({
        plan: selectedPlanName,
        email: user.email ?? user.id,
      });
      analytics.sendPlanUpgredeFlowStartedEvent({
        email: user.email ?? user.id,
      });
      
      // Ödeme işlemine devam et
      await processPayment(method, selectedPlanName, selectedPlanId);
    } catch (error) {
      console.error("Error updating user subscription type:", error);
      setLoading(false);
    }
  };
  
  const processPayment = async (method: "shopify" | "chargebee", name: string, planId?: string) => {
    try {
      if (isDebugMode) {
        if (method === "shopify") {
          window.location.href = "/auth/redirect";
        } else {
          const userEmail = encodeURIComponent(user.email || "");
          const userName = encodeURIComponent(user.username || "");
          await fetch(
            `${chargebeeBackendBaseUrl}/create-checkout?planId=${planId}&customerMail=${userEmail}&customerName=${userName}&redirect_url=${window.location.href}`
          ).then(async (response) => {
            if (response.ok) {
              const data = await response.json();
              window.location.href = data.hosted_page.url;
            }
          });
        }
      } else {
        if (
          method === "shopify" ||
          user.gengageShopifyDomain ||
          user.miqShopifyDomain
        ) {
          window.location.href = "/auth/redirect";
        } else {
          const userEmail = encodeURIComponent(user.email || "");
          const userName = encodeURIComponent(user.username || "");
          await fetch(
            `${chargebeeBackendBaseUrl}/create-checkout?planId=${planId}&customerMail=${userEmail}&customerName=${userName}&redirect_url=${window.location.href}`
          ).then(async (response) => {
            if (response.ok) {
              const data = await response.json();
              window.location.href = data.hosted_page.url;
            }
          });
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
      setIsPaymentDialogOpen(false);
    }
  };

  return (
    <DefaultLayout user={user}>
      <div className="min-h-screen">
        <div className="px-4 py-12 mx-auto max-w-7xl bg-white rounded-lg">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-medium mb-3">
              {t('plans.upgrade_plan')}
            </h1>
            <p className="text-gray-600">
              {t('plans.upgrade_plan_description')}
            </p>
            {isDebugMode && !hasShopifyDomain && (
              <div className="mt-4 p-4 bg-blue-50 text-blue-700 rounded-lg max-w-2xl mx-auto">
                <p>
                  {t('plans.no_shopify_store_connected')}
                </p>
              </div>
            )}

            {isDebugMode && (
              <div className="mt-4 p-4 bg-yellow-50 text-yellow-700 rounded-lg max-w-2xl mx-auto">
                <p className="mb-2 font-semibold">
                  {t('plans.debug_mode_payment')}
                </p>
                <div className="flex justify-center items-center gap-4">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="payment-method"
                      className="sr-only peer"
                      checked={paymentMethod === "shopify"}
                      onChange={() => setPaymentMethod("shopify")}
                    />
                    <div
                      className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                        paymentMethod === "shopify"
                          ? "peer-checked:bg-blue-600"
                          : ""
                      }`}
                    ></div>
                    <span className="ms-3 text-sm font-medium text-gray-900">
                      Shopify
                    </span>
                  </label>

                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="payment-method"
                      className="sr-only peer"
                      checked={paymentMethod === "chargebee"}
                      onChange={() => setPaymentMethod("chargebee")}
                    />
                    <div
                      className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                        paymentMethod === "chargebee"
                          ? "peer-checked:bg-blue-600"
                          : ""
                      }`}
                    ></div>
                    <span className="ms-3 text-sm font-medium text-gray-900">
                      Chargebee
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-center mb-12">
            <Tabs defaultValue={selectedTab} onValueChange={setSelectedTab}>
              <TabsList className="grid w-full max-w-md grid-cols-2 bg-transparent gap-4">
                <TabsTrigger
                  value="monthly"
                  className="rounded-xl px-6 py-3 text-base font-medium transition-all
                    data-[state=active]:bg-blue-50 data-[state=active]:text-gray-900
                    data-[state=inactive]:bg-white data-[state=inactive]:text-gray-500"
                >
                  {t('plans.monthly')}
                </TabsTrigger>
                <TabsTrigger
                  value="yearly"
                  className="w-fit flex items-center justify-between rounded-xl px-4 sm:px-6 py-3 text-base font-medium transition-all
                    data-[state=active]:bg-blue-50 data-[state=active]:text-gray-900
                    data-[state=inactive]:bg-white data-[state=inactive]:text-gray-500"
                >
                  <span className="mr-2">{t('plans.yearly')}</span>
                  <Badge className="whitespace-nowrap bg-blue-500 hover:bg-blue-600 text-white text-xs sm:text-sm">
                    {t('plans.save_percentage')}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {Object.values(PaymentPlanId).map((planId) => {
              const isPopular = planId === PaymentPlanId.Essential;
              const generatedName =
                paymentPlanCards[planId].name +
                " " +
                selectedTab.charAt(0).toUpperCase() +
                selectedTab.slice(1).toLowerCase();
              let isSubscribed = false;
              !subscriptionsError &&
              subscriptionsError === null &&
              !isLoadingSubscriptions &&
              subscriptions &&
              subscriptions.length > 0
                ? (isSubscribed = subscriptions[0].plan.name === generatedName)
                : false;

              return (
                <Card
                  key={planId}
                  className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl 
                      bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700
                      ${
                        isPopular
                          ? "border-blue-500 dark:border-blue-400 shadow-md scale-105"
                          : ""
                      } flex flex-col min-h-[480px]`}
                >
                  {isPopular && (
                    <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 rounded-bl-lg text-sm font-medium">
                      {t('plans.popular')}
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                      {paymentPlanCards[planId].name}
                      {isPopular && (
                        <Sparkles className="h-5 w-5 text-blue-400" />
                      )}
                    </CardTitle>
                    <CardDescription className="text-gray-600 dark:text-gray-300">
                      {paymentPlanCards[planId].description}
                    </CardDescription>
                    <div className="mt-4">
                      <div className="flex items-stretch">
                        <span className="text-4xl font-bold text-gray-900 dark:text-white">
                          {selectedTab === "monthly"
                            ? paymentPlanCards[planId].price.monthly
                            : paymentPlanCards[planId].price.yearly}
                        </span>
                        <div className="ml-2 text-sm text-gray-500 dark:text-gray-400 flex flex-col justify-end">
                          <div>USD</div>
                          <div>
                            {selectedTab === "monthly"
                              ? t('plans.per_month')
                              : t('plans.per_month_billed_yearly')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-4">
                      {paymentPlanCards[planId].features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2">
                          <Check className="h-5 w-5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                          <span className="text-gray-600 dark:text-gray-300">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter className="mt-auto">
                    <Button
                      onClick={() =>
                        handleSelectPlan(
                          generatedName,
                          paymentPlanCards[planId].planId[
                            selectedTab as "monthly" | "yearly"
                          ]
                        )
                      }
                      className={`w-full ${
                        isPopular
                          ? "bg-blue-500 hover:bg-blue-600 text-white"
                          : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-300"
                      }`}
                      disabled={isSubscribed || loading}
                    >
                      {loading && (
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      )}
                      {isSubscribed ? t('plans.current_plan') : t('plans.select_plan')}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
          
          <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader className="pb-2">
                <DialogTitle className="text-center text-xl font-bold">{t('plans.choose_payment_method')}</DialogTitle>
                <DialogDescription className="text-center mt-1 text-sm">
                  {t('plans.select_payment_provider')}
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3 py-3">
                <Button
                  variant="outline"
                  className={`flex items-center justify-start p-3 h-16 ${
                    shopifyDisabled 
                      ? 'opacity-50 cursor-not-allowed bg-gray-100' 
                      : user.subscriptionType === "SHOPIFY" 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'hover:bg-blue-50'
                  }`}
                  onClick={() => handlePaymentMethodSelect("shopify")}
                  disabled={shopifyDisabled || loading}
                >
                  <ShoppingBag className={`h-6 w-6 mr-3 ${user.subscriptionType === "SHOPIFY" ? 'text-blue-600' : 'text-blue-500'}`} />
                  <div className="flex flex-col items-start">
                    <span className={`text-base font-medium ${user.subscriptionType === "SHOPIFY" ? 'text-blue-700' : ''}`}>Shopify</span>
                    <span className="text-xs text-gray-500">{t('plans.pay_with_shopify')}</span>
                  </div>
                  {user.subscriptionType === "SHOPIFY" && (
                    <div className="ml-auto bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                      {t('plans.selected')}
                    </div>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className={`flex items-center justify-start p-3 h-16 ${
                    chargebeeDisabled 
                      ? 'opacity-50 cursor-not-allowed bg-gray-100' 
                      : user.subscriptionType === "CHARGEBEE" 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'hover:bg-blue-50'
                  }`}
                  onClick={() => handlePaymentMethodSelect("chargebee")}
                  disabled={chargebeeDisabled || loading}
                >
                  <CreditCard className={`h-6 w-6 mr-3 ${user.subscriptionType === "CHARGEBEE" ? 'text-blue-600' : 'text-blue-500'}`} />
                  <div className="flex flex-col items-start">
                    <span className={`text-base font-medium ${user.subscriptionType === "CHARGEBEE" ? 'text-blue-700' : ''}`}>Chargebee</span>
                    <span className="text-xs text-gray-500">{t('plans.pay_with_credit_card')}</span>
                  </div>
                  {user.subscriptionType === "CHARGEBEE" && (
                    <div className="ml-auto bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                      {t('plans.selected')}
                    </div>
                  )}
                </Button>
              </div>
              <DialogFooter className="flex justify-between items-center pt-2">
                <Button
                  variant="ghost"
                  onClick={() => setIsPaymentDialogOpen(false)}
                  disabled={loading}
                  className="text-sm"
                >
                  {t('common.cancel')}
                </Button>
                {loading && (
                  <div className="flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <span className="text-sm">{t('plans.processing')}</span>
                  </div>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AdminUpgradePlanPage;
